/**
 * LandingPage — Full marketing page
 *
 * Ported from CopApp 1's ConversionChat.js (1,201 lines).
 * All content, testimonials, stats, and structure preserved.
 * Rewritten in clean React + Tailwind (no MUI, no inline styles).
 */

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { GrantsSection } from './GrantsSection';
import { RequestInfoForm } from './RequestInfoForm';
import { DemoSection } from './DemoSection';

// Real testimonials from CopApp 1
const TESTIMONIALS = [
  {
    name: 'Brittney Hayes',
    title: 'ALEA Lieutenant',
    quote: "I've been messing with the app since it was just an experiment. Its come a long ways and doesn't seem to stop getting better. Its like a puzzle.",
  },
  {
    name: 'Travis Trotter',
    title: 'Guin PD, Officer 17yrs',
    quote: "The reason de-escalation didn't immediately happen is because the need for cooperation and to check everyone's welfare took precedence over de-escalation techniques, but the system did still give me a good rating.",
  },
  {
    name: 'David Cohen',
    title: 'Retired Military, 20yrs',
    quote: "The 'Based Take' is a great way to learn, because instead of a clean and tidy debrief, its written from the perspective of a grizzled old police chief sick of your shit.",
  },
  {
    name: 'Chief Daryl Spencer',
    title: 'Guin Police Department',
    quote: 'I was excited to help develop the product by inviting the concept into our team. Even though we\'re small, I can see it helping.',
  },
];

const AUDIENCE_CONTENT: Record<string, { title: string; subtitle: string; points: string[]; cta: string }> = {
  officers: {
    title: 'For Officers',
    subtitle: 'Train Like You Play',
    points: [
      'Dynamic scenarios that adapt to your responses - not scripted modules',
      'Practice high-stakes situations without real-world consequences',
      'Build muscle memory for de-escalation through repetition',
      'Climb a training tier ladder earned over your recent calls — never one lucky run',
      "Get the 'Based Take' - brutally honest feedback from a grizzled chief perspective",
    ],
    cta: 'Start Training Now',
  },
  academy: {
    title: 'For Police Academies',
    subtitle: 'Next-Gen Cadet Training',
    points: [
      'Replace static role-play exercises with dynamic AI scenarios',
      'Prepare cadets for real-world communication challenges',
      'Track individual and cohort progress throughout training',
      'Build confidence before field training officer assignments',
      'Standardized yet adaptive curriculum for consistent excellence',
    ],
    cta: 'Transform Your Academy',
  },
  chiefs: {
    title: 'For Department Leadership',
    subtitle: 'Real Insights, Real Results',
    points: [
      'Identify communication strengths and blind spots across your force',
      'Track officer progress with detailed performance analytics',
      'Prevent issues before they become complaints or incidents',
      'Department-wide licensing with volume discounts',
      'Comprehensive reporting for training compliance and certification',
    ],
    cta: 'Schedule Department Demo',
  },
  insurance: {
    title: 'For Risk Management',
    subtitle: 'Measurable Risk Reduction',
    points: [
      'Documented training in de-escalation techniques',
      'Verifiable skill assessment beyond attendance records',
      'Reduced liability through proactive training',
      'Track department-wide compliance and proficiency',
      'Lower premiums for departments with exceptional training metrics',
    ],
    cta: 'View Risk Metrics',
  },
  politicians: {
    title: 'For Civic Leadership',
    subtitle: 'Bridge Community Trust',
    points: [
      'Demonstrate commitment to police reform and training',
      'Community Connect feature shows training transparency',
      'Data-driven approach to public safety improvement',
      'Measurable outcomes for legislative reporting',
      'Build public confidence through verifiable officer preparedness',
    ],
    cta: 'Learn More',
  },
  civilians: {
    title: 'For Communities',
    subtitle: 'See Your Officers Train',
    points: [
      'Browse public training sessions from local departments',
      'Challenge your local officers on the same scenarios',
      'Verify officer certifications and training records',
      'Community Connect puts transparency at your fingertips',
      'Building trust through visible, measurable training commitment',
    ],
    cta: 'Explore Community Connect',
  },
};

const IMPACT_STATS = [
  { value: '28%', label: 'Reduction in Use-of-Force', detail: 'Louisville Metro PD using ICAT simulation training', source: 'ICAT RCT Study, Louisville Metro PD', color: 'text-blue-400' },
  { value: '36%', label: 'Fewer Officer Injuries', detail: 'Through verbal de-escalation tactics', source: 'ICAT Program Evaluation, 500+ Officers', color: 'text-blue-400' },
  { value: '81%', label: 'Higher Confidence', detail: 'Officers report improved community interactions', source: 'VR Training Post-Survey Analysis', color: 'text-blue-400' },
  { value: '26%', label: 'Fewer Civilian Injuries', detail: 'Safer resolutions through communication', source: 'ICAT Training Evaluation, Louisville Metro', color: 'text-green-400' },
  { value: '40%', label: 'Less Force in Crisis', detail: 'Mental health crisis interventions', source: 'Canadian VR Crisis Training Study', color: 'text-green-400' },
  { value: '75%+', label: 'Immediate Application', detail: 'Skills transfer to real-world encounters', source: 'ICAT Post-Training Survey, 60-Day Follow-up', color: 'text-green-400' },
];

export function LandingPage() {
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('officers');
  const [testimonialIdx, setTestimonialIdx] = useState(0);
  const [isPaused, setIsPaused] = useState(false);

  // Auto-rotate testimonials
  useEffect(() => {
    if (isPaused) return;
    const interval = setInterval(() => {
      setTestimonialIdx((prev) => (prev + 1) % TESTIMONIALS.length);
    }, 6000);
    return () => clearInterval(interval);
  }, [isPaused]);

  const audience = AUDIENCE_CONTENT[activeTab]!;

  const handleCTA = () => {
    if (isAuthenticated) {
      navigate('/dashboard');
    } else {
      navigate('/signup');
    }
  };

  return (
    <div className="min-h-screen bg-copapp-dark text-white">
      {/* Fixed Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-black/60 backdrop-blur-sm border-b border-blue-400/20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div className="flex items-center">
              <span className="text-xl font-semibold text-white">CopApp</span>
            </div>
            {/* Mobile: one door, the money door — Funding */}
            <a href="/grants" className="md:hidden text-sm font-medium text-white/90 hover:text-white px-3 py-1.5 rounded-lg border border-blue-400/40 transition-colors">
              Funding
            </a>
            <nav className="hidden md:flex items-center space-x-6">
              <a href="#demo" className="text-white/80 hover:text-white transition-colors text-sm">Demo</a>
              <a href="#funding" className="text-white/80 hover:text-white transition-colors text-sm">Funding</a>
              <a href="/community" className="text-white/80 hover:text-white transition-colors text-sm">Community</a>
              {isAuthenticated ? (
                <button onClick={() => navigate('/dashboard')} className="bg-blue-600 hover:bg-blue-700 text-white px-5 py-2 rounded-lg text-sm transition-colors">
                  Dashboard
                </button>
              ) : (
                <>
                  <button onClick={() => navigate('/login')} className="text-white/80 hover:text-white transition-colors text-sm">Sign In</button>
                  <button onClick={handleCTA} className="bg-blue-600 hover:bg-blue-700 text-white px-5 py-2 rounded-lg text-sm transition-colors">
                    Start Free Trial
                  </button>
                </>
              )}
            </nav>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative pt-20 min-h-screen flex items-center">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: "url('/img/hero.jpg')" }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-copapp-dark/60 via-copapp-dark/75 to-copapp-dark" />
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-1/3 left-1/4 w-[500px] h-[500px] bg-blue-600/8 rounded-full blur-[150px]" />
          <div className="absolute bottom-1/4 right-1/3 w-[400px] h-[400px] bg-blue-400/5 rounded-full blur-[120px]" />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="text-center">
            <h1 className="text-5xl sm:text-6xl md:text-7xl font-bold text-white mb-6">
              Practice the calls
              <span className="block text-blue-400">that do not follow a script.</span>
            </h1>
            <p className="max-w-3xl mx-auto text-xl text-white/80 mb-8">
              Step into a generated call, respond as you would on scene, and watch
              the people and situation react. Debrief it. Run it back.
            </p>
            <div className="flex flex-col sm:flex-row justify-center gap-4">
              <button onClick={handleCTA} className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-4 rounded-lg text-lg font-medium transition-all hover:scale-[1.02]">
                {isAuthenticated ? 'Take the Next Call' : 'Start Free Trial'}
              </button>
              <a href="#demo" className="bg-white/10 hover:bg-white/20 text-white px-8 py-4 rounded-lg text-lg font-medium backdrop-blur-sm border border-white/20 transition-all">
                Try the Demo — No Sign-Up
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* The ride-along — the real product, embedded live (docs/DEMO.md) */}
      <DemoSection />

      {/* Problem / Solution */}
      <section id="learn-more" className="py-16 border-t border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">Traditional Training Falls Short</h2>
              <div className="space-y-4 text-white/70">
                {['Static modules that officers click through without engagement',
                  'Quiz scores that only measure memorization, not real skills',
                  'No way to identify communication blind spots before incidents',
                  'Expensive in-person training with limited scenario variety',
                ].map((text, i) => (
                  <p key={i} className="flex items-start"><span className="text-red-500 mr-3 text-xl shrink-0">—</span>{text}</p>
                ))}
              </div>
            </div>
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">CopApp Changes Everything</h2>
              <div className="space-y-4 text-white/70">
                {["Dynamic AI that responds uniquely to each officer's approach",
                  'Performance metrics that reveal actual de-escalation capabilities',
                  'Department analytics to proactively address training needs',
                  'Unlimited scenarios accessible 24/7 from any device',
                ].map((text, i) => (
                  <p key={i} className="flex items-start"><span className="text-green-400 mr-3 text-xl shrink-0">+</span>{text}</p>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Audience Tabs */}
      <section className="py-16 border-t border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">Built for Every Stakeholder</h2>
            <p className="text-xl text-white/70">Select your role to see how CopApp transforms your world</p>
          </div>
          <div className="flex flex-wrap justify-center gap-2 mb-12">
            {Object.keys(AUDIENCE_CONTENT).map((key) => (
              <button
                key={key}
                onClick={() => setActiveTab(key)}
                className={`px-5 py-2.5 rounded-lg text-sm font-medium transition-all ${
                  activeTab === key ? 'bg-blue-600 text-white' : 'bg-white/10 text-white/70 hover:bg-white/20'
                }`}
              >
                {key.charAt(0).toUpperCase() + key.slice(1)}
              </button>
            ))}
          </div>
          <div className="bg-white/5 backdrop-blur-sm rounded-xl p-8 border border-white/10 max-w-4xl mx-auto">
            <h3 className="text-2xl md:text-3xl font-bold text-white mb-2">{audience.title}</h3>
            <p className="text-lg text-blue-400 mb-6">{audience.subtitle}</p>
            <ul className="space-y-3 mb-8">
              {audience.points.map((point, i) => (
                <li key={i} className="flex items-start text-white/80 text-sm">
                  <span className="text-blue-400 mr-3 mt-0.5 shrink-0">→</span>{point}
                </li>
              ))}
            </ul>
            <button onClick={handleCTA} className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-3 rounded-lg font-medium transition-all hover:scale-[1.02]">
              {audience.cta}
            </button>
          </div>
        </div>
      </section>

      {/* Impact Statistics */}
      <section className="py-16 border-t border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <h2 className="text-3xl font-bold text-white mb-4">Proven Results from Communication-Based Training</h2>
            <p className="text-lg text-blue-400">Research-backed outcomes from AI simulation training</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {IMPACT_STATS.map((stat, i) => (
              <div key={i} className="text-center bg-white/5 backdrop-blur-sm rounded-lg p-5">
                <div className={`text-3xl md:text-4xl font-bold mb-2 ${stat.color}`}>{stat.value}</div>
                <p className="text-white/90 font-semibold text-sm">{stat.label}</p>
                <p className="text-white/60 text-xs mt-1">{stat.detail}</p>
                <p className="text-white/40 text-[10px] mt-2 italic">Source: {stat.source}</p>
              </div>
            ))}
          </div>
          <div className="text-center mt-10">
            <p className="text-white/60 italic">"One conversation can change everything"</p>
          </div>
        </div>
      </section>

      {/* Funding — grants, insurance, solicitation form */}
      <GrantsSection />
      <RequestInfoForm variant="landing" />

      {/* Testimonials */}
      <section className="relative py-16 border-t border-white/10">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: "url('/img/community.jpg')" }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-copapp-dark/85 via-copapp-dark/80 to-copapp-dark" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">Field-Tested, Officer-Approved</h2>
            <p className="text-lg text-white/70">Real feedback from law enforcement professionals</p>
          </div>
          <div
            className="relative max-w-3xl mx-auto"
            onMouseEnter={() => setIsPaused(true)}
            onMouseLeave={() => setIsPaused(false)}
          >
            <div className="overflow-hidden">
              <div className="flex transition-transform duration-700 ease-in-out" style={{ transform: `translateX(-${testimonialIdx * 100}%)` }}>
                {TESTIMONIALS.map((t, i) => (
                  <div key={i} className="w-full shrink-0 px-4">
                    <div className={`bg-white/5 backdrop-blur-sm rounded-lg p-8 border text-center transition-all duration-700 ${
                      i === testimonialIdx ? 'border-blue-400/50 shadow-lg shadow-blue-400/10' : 'border-white/10 opacity-50'
                    }`}>
                      <p className="text-white/90 text-lg italic mb-6 leading-relaxed">"{t.quote}"</p>
                      <div className="border-t border-white/10 pt-4">
                        <h3 className="text-base font-semibold text-white">{t.name}</h3>
                        <p className="text-white/60 text-sm">{t.title}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="flex justify-center gap-2 mt-6">
              {TESTIMONIALS.map((_, i) => (
                <button
                  key={i}
                  onClick={() => setTestimonialIdx(i)}
                  className={`transition-all rounded-full ${i === testimonialIdx ? 'w-8 h-2 bg-blue-400' : 'w-2 h-2 bg-white/30 hover:bg-white/50'}`}
                />
              ))}
            </div>
            <button onClick={() => setTestimonialIdx((p) => (p - 1 + TESTIMONIALS.length) % TESTIMONIALS.length)} className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-2 bg-white/10 hover:bg-white/20 text-white p-2 rounded-full">
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
            </button>
            <button onClick={() => setTestimonialIdx((p) => (p + 1) % TESTIMONIALS.length)} className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-2 bg-white/10 hover:bg-white/20 text-white p-2 rounded-full">
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
            </button>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-16 border-t border-white/10">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <h2 className="text-3xl font-bold text-white mb-2">How CopApp Works</h2>
            <p className="text-white/60">Take the call first. Learn from what happened.</p>
          </div>
          <div className="space-y-5">
            {[
              { icon: '◉', color: 'bg-blue-600/20', title: 'Dynamic AI Scenarios', desc: "Every interaction is unique. Our AI generates unlimited scenarios based on real-world situations, adapting to your responses in real-time." },
              { icon: '✓', color: 'bg-green-600/20', title: 'Real-Time Coaching', desc: "Get turn-by-turn feedback and the famous 'Based Take' — brutally honest insights from a veteran chief's perspective." },
              { icon: '◎', color: 'bg-purple-600/20', title: 'Prove It in Public', desc: 'Publish a call and let civilians try to beat your score — same scenario, same engine, same referee. Department analytics reveal patterns and opportunities for growth.' },
            ].map((f, i) => (
              <div key={i} className="flex items-start space-x-4 group">
                <div className={`shrink-0 w-12 h-12 ${f.color} rounded-lg flex items-center justify-center`}>
                  <span className="text-white text-lg">{f.icon}</span>
                </div>
                <div>
                  <h3 className="text-base font-semibold text-white mb-1">{f.title}</h3>
                  <p className="text-white/60 text-sm">{f.desc}</p>
                </div>
              </div>
            ))}
          </div>
          <div className="mt-8 flex flex-wrap gap-2 justify-center">
            {['Voice & Text Modes', '24/7 Available', 'No VR Required', 'Community Connect', 'Department Insights'].map((tag) => (
              <span key={tag} className="px-3 py-1 bg-white/10 rounded-full text-xs text-white/70">{tag}</span>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative py-24 border-t border-white/10">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: "url('/img/deescalation.jpg')" }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-copapp-dark/90 via-copapp-dark/70 to-copapp-dark/95" />
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">Ready for the next call?</h2>
          <p className="text-lg text-white/70 mb-6">Start with one generated scenario and see where your decisions take it.</p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <button onClick={handleCTA} className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-4 rounded-lg text-lg font-medium transition-all hover:scale-[1.02]">
              Start Free Trial
            </button>
            <a href="#request-info" className="bg-white/10 hover:bg-white/20 text-white px-8 py-4 rounded-lg text-lg font-medium border border-white/20 transition-all">
              Contact for Department Demo
            </a>
          </div>
          <p className="mt-4 text-white/50 text-sm">
            Questions? Call Alexi Derkatsch (Founder) at <a href="tel:+16098150257" className="text-blue-400 hover:underline">+1 (609) 815-0257</a>
          </p>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 border-t-2 border-blue-500/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
            <div className="flex flex-wrap justify-center gap-4 text-sm">
              <a href="/terms" className="text-white/70 hover:text-white transition-colors">Terms</a>
              <span className="text-white/40">•</span>
              <a href="/privacy" className="text-white/70 hover:text-white transition-colors">Privacy</a>
              <span className="text-white/40">•</span>
              <a href="/delete-account" className="text-white/70 hover:text-white transition-colors">Delete account</a>
              <span className="text-white/40">•</span>
              <a href="mailto:alexi@copapp.ai" className="text-white/70 hover:text-white transition-colors">Contact</a>
            </div>
            <div className="text-white/50 text-sm">© {new Date().getFullYear()} bigBespoke LLC</div>
            <div className="flex gap-6">
              <a href="https://www.facebook.com/profile.php?id=61574978379037" target="_blank" rel="noopener noreferrer" className="text-white/70 hover:text-blue-400 transition-colors">
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
              </a>
              <a href="https://x.com/CopApp_ai" target="_blank" rel="noopener noreferrer" className="text-white/70 hover:text-blue-400 transition-colors">
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
